import { create } from 'zustand'

import type { OrderSummaryData } from 'lib/components/OrderSummary/OrderSummary'

import type React from 'react'

export interface BookingFlowLayoutProps {
  /**
   * Terms checkbox state
   */
  terms: boolean
  /**
   * Terms error message
   */
  termsError?: string
  /**
   * Handler for terms checkbox change
   */
  setTerms: (terms: boolean) => void
  /**
   * Handler for setting terms error
   */
  setTermsError: (error?: string) => void
  /**
   * Contents to appear on top of the booking flow steps
   */
  topContents?: React.ReactNode | null
  /**
   * Contents to appear after the booking flow steps
   */
  bottomContents?: React.ReactNode | null
  /**
   * Hide current step section from automatically rendering
   */
  hideCurrentStep?: boolean
  /**
   * Hide step sections from automatically rendering
   */
  hideSteps?: boolean
  /**
   * Indicates if the order creation is pending
   */
  showLoadingIcon?: boolean
  /**
   * Optional flag to disable CTA
   */
  sidebarCTADisabled?: boolean
  /**
   * True when any pickup leg has Uber selected but no time slot saved.
   * Written by ShipmentHandoffSelectors, read by PaymentMethod on submit.
   */
  hasInvalidUberLeg?: boolean
  /**
   * Set true when the user tries to advance from step 3 while a leg is invalid.
   * Gates whether the inline validation error renders on the time-slot input.
   */
  pickupValidationAttempted?: boolean
  /**
   * Optional label for sidebar CTA
   */
  sidebarCTALabel?: string
  /**
   * Optional label for mobile sidebar CTA
   */
  sidebarMobileCTALabel?: string
  /**
   * Optional handler for sidebar CTA click
   */
  sidebarCTAOnClick?: (data?: OrderSummaryData) => void
  /**
   * Sets the label for the sidebar CTA
   * @param label - The label to set
   */
  setSidebarCTALabel: (label: string) => void
  /**
   * Sets the handler for sidebar CTA click
   * @param onClick - The click handler to set
   */
  setSidebarCTAOnClick: (onClick: (data?: OrderSummaryData) => void) => void
  /**
   * Sets the label for the mobile sidebar CTA
   * @param label - The label to set
   */
  setSidebarMobileCTALabel: (label: string) => void
  /**
   * Sets the contents to appear on top of the booking flow steps
   * @param contents - The contents to set
   */
  setTopContents: (contents: React.ReactNode) => void
  /**
   * Sets multiple layout properties at once
   * @param props - The properties to set
   */
  setLayoutProps: (props: Partial<BookingFlowLayoutProps>) => void
}

const useLayoutPropsStore = create<BookingFlowLayoutProps>((set, get) => ({
  terms: false,
  termsError: undefined,
  setTerms: (terms) => set({ terms, termsError: terms ? undefined : get().termsError }),
  setTermsError: (termsError) => set({ termsError }),
  topContents: null,
  bottomContents: null,
  hideCurrentStep: false,
  hideSteps: false,
  showLoadingIcon: false,
  orderCreationIsPending: false,
  sidebarCTADisabled: undefined,
  hasInvalidUberLeg: undefined,
  pickupValidationAttempted: undefined,
  sidebarCTALabel: undefined,
  sidebarMobileCTALabel: undefined,
  sidebarCTAOnClick: undefined,
  setSidebarCTALabel: (label) => set({ sidebarCTALabel: label }),
  setSidebarCTAOnClick: (onClick) => set({ sidebarCTAOnClick: onClick }),
  setSidebarMobileCTALabel: (label) => set({ sidebarMobileCTALabel: label }),
  setTopContents: (contents) => set({ topContents: contents }),
  setLayoutProps: (props) => set(props),
}))

export default useLayoutPropsStore
